/**
 * InviteScreen — landing di un invite link `/i/:token` (oppure `/i` generico).
 *
 * - Con token + sei loggato: "Accetta" → redeem → ti connetti all'emittente.
 * - Con token + NON loggato: salvo il token e ti mando a creare/sbloccare
 *   l'identità; al ritorno in inbox il redeem parte da solo.
 * - Senza token: onboarding generico (crea identità).
 *
 * Invito "a distanza": se il server risponde PASSCODE_REQUIRED, mostriamo un
 * campo "codice" e si ritenta col passcode (consegnato fuori-banda dal creatore).
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box } from '../components/Frame';
import { useStore } from '../lib/store';
import { hasLocalBackup } from '../lib/pinCrypto';
import { redeemInvite } from '../lib/messaging';
import { auth, signInAnonymously } from '../lib/firebase';

const PENDING_KEY = 'nullum.pendingInvite';

export default function InviteScreen() {
  const navigate = useNavigate();
  const { token } = useParams();
  const { uid, sigil, mnemonic } = useStore();
  const loggedIn = !!(uid && sigil && mnemonic);
  // "+ Nuova identità" (dal selettore account) imposta questo flag: anche se sul
  // device esiste già un backup, l'utente vuole CREARNE una nuova, non sbloccare
  // quella esistente. Senza il flag, con backup presente offriremmo "Sblocca".
  const wantNew = (() => { try { return sessionStorage.getItem('nullum.wantNewIdentity') === '1'; } catch { return false; } })();
  const unlockPath = hasLocalBackup() && !wantNew; // true → sblocca; false → crea nuova

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [needPasscode, setNeedPasscode] = useState(false);
  const [passcode, setPasscode] = useState('');

  function mapErr(e) {
    const m = (e?.message || '').toUpperCase();
    if (m.includes('PASSCODE_REQUIRED')) return 'Serve il codice che ti ha dato chi ti ha invitato. Inseriscilo qui sotto.';
    if (m.includes('PASSCODE_WRONG')) return 'Codice errato. Controlla e riprova.';
    if (m.includes('REVOKED')) return 'questo invito è stato annullato dal mittente.';
    if (m.includes('ALREADY_USED')) return 'questo invito è già stato usato.';
    if (m.includes('EXPIRED')) return 'questo invito è scaduto.';
    if (m.includes('NOT_FOUND')) return 'invito non valido.';
    if (m.includes('CANNOT_REDEEM_OWN')) return 'è il tuo stesso invito.';
    return e?.message || 'errore';
  }
  // Se il server chiede/rifiuta il codice, riveliamo il campo passcode.
  function handleRedeemErr(e) {
    const m = (e?.message || '').toUpperCase();
    if (m.includes('PASSCODE_REQUIRED') || m.includes('PASSCODE_WRONG')) setNeedPasscode(true);
    setErr(mapErr(e));
  }

  async function accept() {
    setErr(null);
    setBusy(true);
    try {
      const r = await redeemInvite(token, passcode.trim() || null);
      if (!r?.ok) throw new Error(r?.error || 'INVITE_NOT_FOUND');
      sessionStorage.setItem('nullum.connectTo', r.creator.sigil);
      // Salva anche il creator completo (sigil + uid + pubKeyBox derivato-aware)
      // così NewChatScreen non deve ri-cercare (i derivati privati non si trovano).
      try { sessionStorage.setItem('nullum.connectToFull', JSON.stringify(r.creator)); } catch {}
      sessionStorage.removeItem(PENDING_KEY);
      navigate('/new');
    } catch (e) {
      handleRedeemErr(e);
    } finally {
      setBusy(false);
    }
  }

  // Auth a token custom (Opzione 2): l'invito va riscattato DALL'IDENTITÀ (così
  // `invited` e la whitelist sono legati all'uid reale, non a un anon effimero).
  // Qui NON bruciamo il link: validiamo subito con dryRun (feedback precoce su
  // link usato/scaduto/passcode), stashiamo token+passcode, e il redeem VERO
  // avverrà dall'identità — al claim per i nuovi, in inbox per chi sblocca.
  async function startOnboardThenRedeem() {
    setErr(null);
    if (!token) {
      navigate(hasLocalBackup() ? '/pin-unlock' : '/generate');
      return;
    }
    setBusy(true);
    try {
      // 1. Anon effimero: redeemInvite (anche in dryRun) richiede auth.
      if (!auth().currentUser) {
        try { await signInAnonymously(auth()); }
        catch (e) {
          setErr('Serve connessione: riprova quando sei online.');
          return;
        }
      }
      // 2. Valida SENZA bruciare (dryRun): se il link è usato/revocato/scaduto o
      //    manca il passcode, lo scopriamo QUI prima dell'onboarding.
      const r = await redeemInvite(token, passcode.trim() || null, { dryRun: true });
      if (!r?.ok) {
        throw new Error(r?.error || 'INVITE_NOT_FOUND');
      }
      // 3. Stash per il redeem differito (dall'identità).
      try {
        sessionStorage.setItem(PENDING_KEY, token);
        if (passcode.trim()) sessionStorage.setItem('nullum.pendingInvitePass', passcode.trim());
        else sessionStorage.removeItem('nullum.pendingInvitePass');
        sessionStorage.removeItem('nullum.connectTo');
        sessionStorage.removeItem('nullum.connectToFull');
      } catch {}
      // 4. Avanti: crea una NUOVA identità (intento esplicito o nessun backup)
      //    oppure sblocca quella locale. Consumo il flag "voglio una nuova".
      try { sessionStorage.removeItem('nullum.wantNewIdentity'); } catch {}
      navigate(unlockPath ? '/pin-unlock' : '/generate');
    } catch (e) {
      handleRedeemErr(e);
    } finally {
      setBusy(false);
    }
  }

  // Campo codice condiviso (mostrato solo quando il server lo richiede).
  const passcodeField = needPasscode ? (
    <div className="mt-3">
      <label className="block text-[13px] text-ink-dim mb-1">&gt; CODICE INVITO</label>
      <input
        type="text"
        value={passcode}
        onChange={(e) => setPasscode(e.target.value.toUpperCase())}
        placeholder="es. ABCD2345"
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck="false"
        className="w-full bg-black border border-ink-dim text-ink font-mono text-[18px] tracking-[0.2em] px-3 py-2 focus:border-ink focus:outline-none"
      />
      <p className="text-[12px] text-ink-dim mt-1">
        te lo dà chi ti ha invitato, a voce o su un altro canale.
      </p>
    </div>
  ) : null;

  // --- Caso 1: invito con token, utente loggato → accetta e connetti
  if (token && loggedIn) {
    return (
      <Frame statusLeft="INVITO" statusRight="5G">
        <Header title="▼ Sei stato invitato" amber crumb="◀" onCrumbClick={() => navigate('/inbox')} />
        <Box variant="success" className="text-center mb-3 p-4">
          <div className="text-2xl text-glow my-1">NULLUM</div>
          <p className="text-[14px] text-ink-dim mt-1">qualcuno vuole connettersi con te</p>
        </Box>
        <p className="text-[15px] text-ink-dim">
          accetta e potrai scrivergli. il suo @sigil te lo mostriamo solo ora, dopo l'accettazione.
        </p>
        {passcodeField}
        {err && <Box variant="danger" className="text-[14px] mt-3"><span className="text-red-500">✕</span> {err}</Box>}
        <Footer>
          <Btn variant="primary" onClick={accept} disabled={busy || (needPasscode && !passcode.trim())}>
            {busy ? '...' : (needPasscode ? '✓ Verifica e accetta' : '✓ Accetta e connettiti')}
          </Btn>
          <Btn variant="ghost" onClick={() => navigate('/inbox')}>Annulla</Btn>
        </Footer>
      </Frame>
    );
  }

  // --- Caso 2: invito con token, NON loggato → crea/sblocca, poi redeem
  if (token && !loggedIn) {
    return (
      <Frame statusLeft="INVITO" statusRight="5G">
        <Header title="▼ Sei stato invitato" amber />
        <Box variant="success" className="text-center mb-3 p-4">
          <div className="text-2xl text-glow my-1">NULLUM</div>
          <p className="text-[14px] text-ink-dim mt-1">messaggi anonimi che legge solo chi vuoi tu</p>
        </Box>
        <p className="text-[15px] text-ink-dim mb-2">
          per accettare ti serve un'identità Nullum.
          {unlockPath ? ' sblocca la tua e l\'invito parte da solo.' : ' creala in pochi secondi.'}
        </p>
        {passcodeField}
        {err && <Box variant="danger" className="text-[14px] mb-3 mt-3"><span className="text-red-500">✕</span> {err}</Box>}
        <Footer>
          <Btn variant="primary" onClick={startOnboardThenRedeem} disabled={busy || (needPasscode && !passcode.trim())}>
            {busy ? '...' : (needPasscode ? '✓ Verifica e continua' : (unlockPath ? '🔓 Sblocca e accetta' : '✦ Crea identità e accetta'))}
          </Btn>
        </Footer>
      </Frame>
    );
  }

  // --- Caso 3: nessun token → registrazione SOLO su invito
  return (
    <Frame statusLeft="INVITO" statusRight="5G">
      <Header title="▼ Nullum è su invito" crumb="◀ indietro" onCrumbClick={() => navigate('/')} amber />
      <Box variant="success" className="text-center mb-3 p-4">
        <div className="text-2xl text-glow my-1">NULLUM</div>
        <p className="text-[14px] text-ink-dim mt-1">messaggi anonimi che legge solo chi vuoi tu</p>
      </Box>
      <Box variant="warn" className="text-[14px] text-ink-dim leading-relaxed">
        <p className="text-amber mb-1">🔒 Serve un invito</p>
        <p>Si entra in Nullum <b className="text-ink">solo con un link di invito</b>
        {' '}di chi è già dentro. Apri quel link per creare la tua identità.</p>
      </Box>
      <Footer>
        <Btn onClick={() => navigate('/restore')}>↺ Ho già un account (sigil + PIN)</Btn>
        <Btn variant="ghost" onClick={() => navigate('/recover')}>Ho perso il PIN — uso le 24 parole</Btn>
      </Footer>
    </Frame>
  );
}
